/**
 * ClassCodeModal.js - Modal for viewing and managing class code snippets.
 * Admin: Can create, edit, delete code snippets (uses main page section dropdown)
 * Student: Can view, run, get explanations, and save memos
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Editor from '@monaco-editor/react';
import JSZip from 'jszip';
import { API_BASE_URL, PYTHON_INTERPRETER_URL, logger, apiFetch, isDemoMode } from '../config';
import VisualDebugger from './VisualDebugger';

// Simple component that renders code with preserved whitespace — no JSX whitespace issues
const CodeBlock = ({ code, style }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.textContent = code;
  }, [code]);
  return <div ref={ref} style={{ textAlign: 'left', ...style }} />;
};

const ClassCodeModal = ({ username, userRole, onClose, selectedSection, onLoadCode }) => {
  // Get class info from localStorage - use selectedSection from main page dropdown
  const classSection = selectedSection || (localStorage.getItem('class_section') || '').split(',')[0].trim();
  const semester = localStorage.getItem('semester') || '';
  const university = localStorage.getItem('university') || '';

  const isAdmin = userRole === 'admin';

  // Track Monaco disposables for cleanup
  const completionDisposableRef = useRef(null);

  const handleEditorDidMount = useCallback((editor, monaco) => {
    // Dispose previous provider if any
    if (completionDisposableRef.current) {
      completionDisposableRef.current.dispose();
      completionDisposableRef.current = null;
    }

    // ===== NUCLEAR OPTION: Disable ALL built-in suggest features =====
    // This removes word-based suggestions and any other built-in completion
    editor.updateOptions({
      wordBasedSuggestions: 'off',
      suggest: {
        showWords: false,
        showSnippets: false,
        showUsers: false,
        showIcons: true,
        showStatusBar: false,
        showInlineDetails: true,
        filterGraceful: false,
        localityBonus: false,
      },
      quickSuggestions: { other: true, comments: false, strings: false },
    });

    // Type-specific methods
    var strMethods = ['capitalize','casefold','center','count','encode','endswith','find','format','format_map','index','isalnum','isalpha','isascii','isdecimal','isdigit','isidentifier','islower','isnumeric','isprintable','isspace','istitle','isupper','join','ljust','lower','lstrip','maketrans','partition','removeprefix','removesuffix','replace','rfind','rindex','rjust','rpartition','rsplit','rstrip','split','splitlines','startswith','strip','swapcase','title','translate','upper','zfill'];
    var listMethods = ['append','clear','copy','count','extend','index','insert','pop','remove','reverse','sort'];
    var dictMethods = ['clear','copy','fromkeys','get','items','keys','pop','popitem','setdefault','update','values'];
    var setMethods = ['add','clear','copy','difference','difference_update','discard','intersection','intersection_update','isdisjoint','issubset','issuperset','pop','remove','symmetric_difference','symmetric_difference_update','union','update'];
    var intMethods = ['bit_count','bit_length','conjugate','to_bytes','from_bytes'];
    var floatMethods = ['as_integer_ratio','conjugate','hex','is_integer','fromhex'];
    var tupleMethods = ['count','index'];
    var typeMap = { str: strMethods, list: listMethods, dict: dictMethods, set: setMethods, int: intMethods, float: floatMethods, tuple: tupleMethods };

    var keywords = ['False','None','True','and','as','assert','async','await','break','class','continue','def','del','elif','else','except','finally','for','from','global','if','import','in','is','lambda','nonlocal','not','or','pass','raise','return','try','while','with','yield'];
    var builtins = ['abs','all','any','bin','bool','bytes','callable','chr','dict','dir','divmod','enumerate','eval','exec','filter','float','format','frozenset','getattr','globals','hasattr','hash','help','hex','id','input','int','isinstance','issubclass','iter','len','list','locals','map','max','min','next','object','oct','open','ord','pow','print','property','range','repr','reversed','round','set','setattr','slice','sorted','staticmethod','str','sum','super','tuple','type','vars','zip'];
    var modules = ['os','sys','math','random','datetime','json','csv','re','collections','itertools','functools','string','time','typing','pathlib','io','copy','statistics','numpy','pandas','matplotlib','requests','flask','django','scipy','sklearn','turtle','tkinter','unittest','logging','argparse','subprocess','threading'];

    // Register with '*' (all languages) so we are in the SAME group as the
    // built-in word-based provider. This makes Monaco pick ONLY our results
    // and skip the word-based provider (same-group priority).
    var disp = monaco.languages.registerCompletionItemProvider('*', {
      triggerCharacters: ['.'],
      provideCompletionItems: function(model, position) {
        // Only provide completions for our editor
        if (model !== editor.getModel()) {
          return { suggestions: [] };
        }

        var word = model.getWordUntilPosition(position);
        var range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };
        var line = model.getLineContent(position.lineNumber);
        var before = line.substring(0, position.column - 1);

        // After a dot: show type methods only
        var dm = before.match(/(\w+)\.\w*$/);
        if (dm) {
          var obj = dm[1];
          var meths = typeMap[obj];
          if (!meths) {
            // Try to infer type from variable assignments in the code
            var allText = model.getValue();
            var varPattern = new RegExp('\\b' + obj + '\\s*=\\s*(.+)');
            var varMatch = allText.match(varPattern);
            if (varMatch) {
              var val = varMatch[1].trim();
              if (val.startsWith('"') || val.startsWith("'") || val.startsWith('f"') || val.startsWith("f'")) {
                meths = strMethods;
              } else if (val.startsWith('[')) {
                meths = listMethods;
              } else if (val.startsWith('{') && val.includes(':')) {
                meths = dictMethods;
              } else if (val.startsWith('{') || val.startsWith('set(')) {
                meths = setMethods;
              } else if (val.startsWith('(')) {
                meths = tupleMethods;
              } else if (val.match(/^\d+$/)) {
                meths = intMethods;
              } else if (val.match(/^\d+\.\d+$/)) {
                meths = floatMethods;
              } else if (val.startsWith('str(')) {
                meths = strMethods;
              } else if (val.startsWith('list(')) {
                meths = listMethods;
              } else if (val.startsWith('dict(')) {
                meths = dictMethods;
              } else if (val.startsWith('int(')) {
                meths = intMethods;
              } else if (val.startsWith('float(')) {
                meths = floatMethods;
              } else if (val.startsWith('input(')) {
                meths = strMethods;
              }
            }
            // Fallback: show all methods merged
            if (!meths) {
              var seen = {};
              meths = [];
              [strMethods, listMethods, dictMethods, setMethods, tupleMethods].forEach(function(arr) {
                arr.forEach(function(m) { if (!seen[m]) { seen[m] = true; meths.push(m); } });
              });
            }
          }
          return {
            suggestions: meths.map(function(m, i) {
              return {
                label: m,
                kind: monaco.languages.CompletionItemKind.Method,
                insertText: m,
                range: range,
                detail: (typeMap[obj] ? obj : 'Python') + ' method',
                sortText: String.fromCharCode(65 + Math.floor(i / 26)) + String.fromCharCode(65 + (i % 26))
              };
            })
          };
        }

        // Normal: keywords + builtins + modules (after import)
        var sugs = [];
        keywords.forEach(function(k, i) {
          sugs.push({
            label: k,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: k,
            range: range,
            detail: 'keyword',
            sortText: 'B' + k
          });
        });
        builtins.forEach(function(b) {
          sugs.push({
            label: b,
            kind: monaco.languages.CompletionItemKind.Function,
            insertText: b,
            range: range,
            detail: 'built-in',
            sortText: 'A' + b
          });
        });
        if (before.match(/^\s*(import|from)\s/)) {
          modules.forEach(function(m) {
            sugs.push({
              label: m,
              kind: monaco.languages.CompletionItemKind.Module,
              insertText: m,
              range: range,
              detail: 'module',
              sortText: 'A' + m
            });
          });
        }
        return { suggestions: sugs };
      }
    });

    completionDisposableRef.current = disp;
    console.log('✅ Python IntelliSense registered (wildcard provider for same-group priority)');
  }, []);

  // State
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Admin state
  const [editMode, setEditMode] = useState('new'); // 'new' or 'edit'
  const [selectedCodeId, setSelectedCodeId] = useState(null);
  const [codeName, setCodeName] = useState('');
  const [codeContent, setCodeContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [codeNames, setCodeNames] = useState([]); // For dropdown

  // Student state
  const [expandedCodeId, setExpandedCodeId] = useState(null);
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [memos, setMemos] = useState({}); // { codeId: memoText }
  
  // Code running state
  const [runningCodeId, setRunningCodeId] = useState(null);
  const [runOutput, setRunOutput] = useState({});  // { codeId: output }
  const [executionId, setExecutionId] = useState(null);     // for input() pausing
  const [isAwaitingInput, setIsAwaitingInput] = useState(false);
  const [promptEndIndex, setPromptEndIndex] = useState(0);
  const activeRunIdRef = useRef(null); // tracks which codeId is in the output panel
  const serverStdoutRef = useRef(0);

  // Turtle (Skulpt) state
  const [skulptLoaded, setSkulptLoaded] = useState(false);
  const [turtleRunning, setTurtleRunning] = useState(false);
  const [showTurtlePanel, setShowTurtlePanel] = useState(false);
  const [turtleOutput, setTurtleOutput] = useState('');

  // Visualizer state
  const [showVisualizer, setShowVisualizer] = useState(false);
  const [traceSteps, setTraceSteps] = useState([]);
  const [isTracing, setIsTracing] = useState(false);
  const [showInputModal, setShowInputModal] = useState(false);
  const [inputModalValues, setInputModalValues] = useState('');
  const [pendingTraceCode, setPendingTraceCode] = useState('');

  // Editor mode toggle: true = Monaco, false = simple textarea
  const [useMonaco, setUseMonaco] = useState(false);
  const [editorTheme, setEditorTheme] = useState('vs-dark'); // 'vs-dark' or 'vs'
  const textareaRef = useRef(null);

  // Draggable divider between editor and output
  const [outputHeight, setOutputHeight] = useState(200); // default output panel height in px
  const isDraggingDivider = useRef(false);
  const editorOutputContainerRef = useRef(null);

  // Draggable divider for memo/notes panel
  const [memoHeight, setMemoHeight] = useState(120); // default memo panel height in px
  const isDraggingMemoDivider = useRef(false);

  // Draggable divider between CLASS FILES and MY FILES sections
  const [classFilesHeight, setClassFilesHeight] = useState(null); // null = auto (50%)
  const isDraggingSectionDivider = useRef(false);
  const fileBrowserRef = useRef(null);

  // Two-section file browser state
  const [selectedFileSource, setSelectedFileSource] = useState(null); // 'class' or 'my'
  const [classFilesCollapsed, setClassFilesCollapsed] = useState(false);
  const [myFilesCollapsed, setMyFilesCollapsed] = useState(false);
  const [myFileEditMode, setMyFileEditMode] = useState(null); // null or 'new'
  const [myFileName, setMyFileName] = useState('');

  // Folder state
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [creatingFolderIn, setCreatingFolderIn] = useState(null); // 'class' or 'my' or null
  const [newFolderName, setNewFolderName] = useState('');
  const [activeFolder, setActiveFolder] = useState(null); // folder to create new file in

  // Empty folders persisted in localStorage — keyed per user so they don't leak across accounts
  const emptyFoldersKey = `emptyFolders_${username}_${classSection}_${semester}_${university}`;
  const [emptyFolders, setEmptyFolders] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(emptyFoldersKey)) || { class: [], my: [] };
    } catch { return { class: [], my: [] }; }
  });
  const saveEmptyFolders = (updated) => {
    setEmptyFolders(updated);
    localStorage.setItem(emptyFoldersKey, JSON.stringify(updated));
  };

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { x, y, type: 'folder'|'file', name, id, section: 'class'|'my' }

  // Tutor Help state (debugging chat)
  const [showTutorHelp, setShowTutorHelp] = useState(false);
  const [tutorMessages, setTutorMessages] = useState([]); // { role: 'user'|'assistant', content: string }
  const [tutorInput, setTutorInput] = useState('');
  const [tutorLoading, setTutorLoading] = useState(false);
  const tutorChatRef = useRef(null);
  const [tutorChatHeight, setTutorChatHeight] = useState(280); // default tutor chat height
  const isDraggingTutorDivider = useRef(false);

  // Helper: build folder tree from flat file list
  const buildFolderTree = (files, section) => {
    const rootFiles = [];
    const folders = {};
    files.forEach(f => {
      if (f.name.includes('/')) {
        const slashIdx = f.name.indexOf('/');
        const folder = f.name.substring(0, slashIdx);
        if (!folders[folder]) folders[folder] = [];
        folders[folder].push({ ...f, displayName: f.name.substring(slashIdx + 1) });
      } else {
        rootFiles.push({ ...f, displayName: f.name });
      }
    });
    // Add empty folders from localStorage
    const sectionEmpty = (section === 'class' ? emptyFolders.class : emptyFolders.my) || [];
    sectionEmpty.forEach(fn => {
      if (!folders[fn]) folders[fn] = [];
    });
    // Add activeFolder if it's for this section and not yet in list
    if (activeFolder && ((section === 'class' && selectedFileSource === 'class') || (section === 'my' && selectedFileSource === 'my'))) {
      if (!folders[activeFolder]) folders[activeFolder] = [];
    }
    return { rootFiles, folders };
  };

  const toggleFolder = (folderKey) => {
    setCollapsedFolders(prev => ({ ...prev, [folderKey]: !prev[folderKey] }));
  };

  // Helper: is folder open? Default is CLOSED (collapsed)
  const isFolderOpen = (folderKey) => collapsedFolders[folderKey] === true;

  // Close context menu on Escape key
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setContextMenu(null); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  // Clean up empty folders that now have files in them
  useEffect(() => {
    if (!codeNames.length) return;
    const usedFolders = new Set();
    codeNames.forEach(cn => {
      if (cn.name.includes('/')) usedFolders.add(cn.name.substring(0, cn.name.indexOf('/')));
    });
    let changed = false;
    const updatedClass = (emptyFolders.class || []).filter(fn => {
      if (usedFolders.has(fn)) { changed = true; return false; }
      return true;
    });
    const updatedMy = (emptyFolders.my || []).filter(fn => {
      if (usedFolders.has(fn)) { changed = true; return false; }
      return true;
    });
    if (changed) saveEmptyFolders({ class: updatedClass, my: updatedMy });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeNames]);

  // Load codes list
  const loadCodes = useCallback(async () => {
    if (!classSection || !semester || !university) {
      setError('Missing class section, semester, or university information.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/classCode/list?class_section=${encodeURIComponent(classSection)}&semester=${encodeURIComponent(semester)}&university=${encodeURIComponent(university)}`
      );
      if (res.ok) {
        const data = await res.json();
        setCodes(data.codes || []);
      } else {
        setError('Failed to load class codes');
      }
    } catch (err) {
      logger.error('Error loading class codes:', err);
      setError('Error loading class codes');
    }
    setLoading(false);
  }, [classSection, semester, university]);

  // Load code names for file browser
  const loadCodeNames = useCallback(async () => {
    if (!classSection || !semester || !university) return;

    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/classCode/names?class_section=${encodeURIComponent(classSection)}&semester=${encodeURIComponent(semester)}&university=${encodeURIComponent(university)}`
      );
      if (res.ok) {
        const data = await res.json();
        setCodeNames(data.names || []);
      }
    } catch (err) {
      logger.error('Error loading code names:', err);
    }
  }, [classSection, semester, university]);

  // Load memos from localStorage
  const loadMemos = useCallback(() => {
    const loadedMemos = {};
    codes.forEach(code => {
      const memo = localStorage.getItem(`memo_classCode_${code.id}`);
      if (memo) loadedMemos[code.id] = memo;
    });
    setMemos(loadedMemos);
  }, [codes]);

  useEffect(() => {
    loadCodes();
    loadCodeNames();
  }, [loadCodes, loadCodeNames]);

  useEffect(() => {
    if (codes.length > 0) {
      loadMemos();
    }
  }, [codes, loadMemos]);

  // Generate explanation using OpenAI (called by admin on save/update)
  const generateExplanation = async (codeText) => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: 'You are a helpful Python tutor. Explain the following code in a clear, beginner-friendly way. Break down what each part does and explain any important concepts.'
            },
            {
              role: 'user',
              content: `Please explain this Python code:\n\n\`\`\`python\n${codeText}\n\`\`\``
            }
          ],
          temperature: 0.7
        })
      });

      const data = await res.json();
      if (res.ok && data.choices && data.choices[0]) {
        return data.choices[0].message.content;
      }
      return null;
    } catch (err) {
      logger.error('Error generating explanation:', err);
      return null;
    }
  };

  // Auto-save on close: close immediately, save in background
  const handleClose = () => {
    const name = codeName.trim();
    const code = codeContent.trim();

    // Fire-and-forget: save in background without blocking close
    if (code && name) {
      const doSave = async () => {
        try {
          const generatedExplanation = await generateExplanation(code) || '';

          if (editMode === 'edit' && selectedCodeId) {
            await apiFetch(`${API_BASE_URL}/api/classCode/update`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: selectedCodeId,
                name: name,
                code: code,
                explanation: generatedExplanation
              })
            });
          } else if (editMode === 'new' && activeFolder) {
            let fullName = name;
            if (activeFolder && !fullName.startsWith(activeFolder + '/')) {
              fullName = activeFolder + '/' + fullName;
            }
            fullName = fullName.replace(/\/+$/, '');
            if (fullName.includes('/')) {
              await apiFetch(`${API_BASE_URL}/api/classCode/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: fullName,
                  code: code,
                  explanation: generatedExplanation,
                  class_section: classSection,
                  semester: semester,
                  university: university,
                  created_by: isAdmin ? 'admin' : username
                })
              });
            }
          }
        } catch (err) {
          logger.error('Auto-save on close failed:', err);
        }
      };
      doSave(); // fire and forget — don't await
    }

    onClose(); // close immediately
  };

  // Admin: Save new code
  const handleSaveNew = async () => {
    // Build the full name including folder prefix if activeFolder is set
    let fullName = codeName.trim();
    if (activeFolder && !fullName.startsWith(activeFolder + '/')) {
      fullName = activeFolder + '/' + fullName;
    }
    // Remove trailing slash (folder without filename)
    fullName = fullName.replace(/\/+$/, '');

    if (!fullName || !codeContent.trim()) {
      setError('Please enter both name and code');
      return;
    }
    // Ensure folder files actually have a filename after the slash
    if (activeFolder && !fullName.includes('/')) {
      setError('Please enter a file name');
      return;
    }
    if (activeFolder && fullName.endsWith('/')) {
      setError('Please enter a file name after the folder');
      return;
    }

    setSaving(true);
    setError('');
    try {
      // Save immediately WITHOUT waiting for explanation (fast save)
      const payload = {
        name: fullName,
        code: codeContent,
        explanation: '', // Empty initially - will be updated async
        class_section: classSection,
        semester: semester,
        university: university,
        created_by: isAdmin ? 'admin' : username
      };
      const res = await apiFetch(`${API_BASE_URL}/api/classCode/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok && data.success) {
        // Keep the code visible — reload names and auto-select the newly saved file
        const savedName = fullName;
        await loadCodes();
        await loadCodeNames();

        // Find the newly saved file in the updated names list and select it
        const namesRes = await apiFetch(
          `${API_BASE_URL}/api/classCode/names?class_section=${encodeURIComponent(classSection)}&semester=${encodeURIComponent(semester)}&university=${encodeURIComponent(university)}`
        );
        let savedFileId = null;
        if (namesRes.ok) {
          const namesData = await namesRes.json();
          const savedFile = (namesData.names || []).find(
            cn => cn.name === savedName && cn.createdBy === (isAdmin ? 'admin' : username)
          );
          if (savedFile) {
            savedFileId = savedFile.id;
            setSelectedCodeId(savedFile.id);
            setEditMode('edit');
          }
        }
        // Reset new-file modes and active folder
        setMyFileEditMode(null);
        setMyFileName('');
        setActiveFolder(null);
        
        // Generate explanation ASYNC in background (fire-and-forget)
        if (savedFileId && codeContent.trim()) {
          const codeToExplain = codeContent;
          // Don't await - let it run in background
          (async () => {
            try {
              const generatedExplanation = await generateExplanation(codeToExplain);
              if (generatedExplanation) {
                await apiFetch(`${API_BASE_URL}/api/classCode/update`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: savedFileId,
                    name: savedName,
                    code: codeToExplain,
                    explanation: generatedExplanation
                  })
                });
                logger.log('Explanation generated and saved in background');
              }
            } catch (err) {
              logger.error('Background explanation generation failed:', err);
            }
          })();
        }
      } else {
        const errMsg = data.error || 'Failed to save code';
        if (errMsg.toLowerCase().includes('duplicate') || errMsg.toLowerCase().includes('unique')) {
          setError(`A file named "${fullName}" already exists. Please choose a different name.`);
        } else {
          setError(errMsg);
        }
      }
    } catch (err) {
      logger.error('Error saving code:', err);
      setError('Error saving code');
    }
    setSaving(false);
  };

  // Admin: Update existing code
  const handleUpdate = async () => {
    if (!selectedCodeId || !codeName.trim() || !codeContent.trim()) {
      setError('Please select a code and enter both name and code');
      return;
    }

    setSaving(true);
    setError('');
    try {
      // Save code immediately WITHOUT waiting for explanation (fast update)
      const res = await apiFetch(`${API_BASE_URL}/api/classCode/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedCodeId,
          name: codeName.trim(),
          code: codeContent
          // Don't include explanation - will update async
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        await loadCodes();
        await loadCodeNames();
        
        // Generate explanation ASYNC in background (fire-and-forget)
        const codeId = selectedCodeId;
        const nameToSave = codeName.trim();
        const codeToExplain = codeContent;
        // Don't await - let it run in background
        (async () => {
          try {
            const generatedExplanation = await generateExplanation(codeToExplain);
            if (generatedExplanation) {
              await apiFetch(`${API_BASE_URL}/api/classCode/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: codeId,
                  name: nameToSave,
                  code: codeToExplain,
                  explanation: generatedExplanation
                })
              });
              logger.log('Explanation updated in background');
            }
          } catch (err) {
            logger.error('Background explanation update failed:', err);
          }
        })();
      } else {
        setError(data.error || 'Failed to update code');
      }
    } catch (err) {
      logger.error('Error updating code:', err);
      setError('Error updating code');
    }
    setSaving(false);
  };

  // Admin: Delete code
  const handleDelete = async () => {
    if (!selectedCodeId) {
      setError('Please select a code to delete');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this code?')) return;

    setSaving(true);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/classCode/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedCodeId })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSelectedCodeId(null);
        setCodeName('');
        setCodeContent('');
        setEditMode('new');
        await loadCodes();
        await loadCodeNames();
      } else {
        setError(data.error || 'Failed to delete code');
      }
    } catch (err) {
      logger.error('Error deleting code:', err);
      setError('Error deleting code');
    }
    setSaving(false);
  };

  // Admin: Load code into editor for editing
  const handleSelectCode = async (id) => {
    if (!id) {
      setEditMode('new');
      setSelectedCodeId(null);
      setCodeName('');
      setCodeContent('');
      return;
    }

    try {
      const res = await apiFetch(`${API_BASE_URL}/api/classCode/byId?id=${id}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.code) {
          setSelectedCodeId(id);
          setCodeName(data.code.name);
          setCodeContent(data.code.code);
          setEditMode('edit');
        }
      }
    } catch (err) {
      logger.error('Error loading code:', err);
    }
  };

  // Download code as .py file
  const handleDownloadFile = () => {
    if (!codeContent.trim()) return;
    const fileName = (codeName ? codeName.replace(/\.py$/, '') : 'untitled') + '.py';
    const blob = new Blob([codeContent], { type: 'text/x-python' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load Skulpt on first use
  const loadSkulpt = () => new Promise((resolve, reject) => {
    if (window.Sk && window.Sk.builtinFiles) { resolve(); return; }
    const s1 = document.createElement('script');
    s1.src = 'https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt.min.js';
    s1.crossOrigin = 'anonymous';
    document.head.appendChild(s1);
    s1.onload = () => {
      const s2 = document.createElement('script');
      s2.src = 'https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt-stdlib.js';
      s2.crossOrigin = 'anonymous';
      document.head.appendChild(s2);
      s2.onload = () => resolve();
      s2.onerror = () => reject(new Error('Failed to load skulpt-stdlib.js'));
    };
    s1.onerror = () => reject(new Error('Failed to load skulpt.min.js'));
  });

  // ── Visualizer ────────────────────────────────────────────────────────────
  const handleVisualize = () => {
    if (!codeContent.trim()) {
      setRunOutput(prev => ({ ...prev, [selectedCodeId || 'new']: '🔒 Step-by-step visualizer is locked in demo mode.\n\nWith an account you can step through your Python code line by line,\nwatching each variable change in real time.\n\n✨ Sign up to unlock!' }));
      return;
    }
    if (isDemoMode()) {
      setRunOutput(prev => ({ ...prev, [selectedCodeId || 'new']: '🔒 Step-by-step visualizer is locked in demo mode.\n\n✨ Sign up to unlock!' }));
      return;
    }
    const uncommentedCode = codeContent.split('\n').map(line => {
      const t = line.trimStart();
      if (t.startsWith('#')) return '';
      const ci = line.indexOf('#');
      return ci >= 0 ? line.slice(0, ci) : line;
    }).join('\n');
    const inputCount = (uncommentedCode.match(/\binput\s*\(/g) || []).length;
    if (inputCount > 0) {
      setPendingTraceCode(codeContent);
      setInputModalValues('');
      setShowInputModal(true);
    } else {
      runTrace(codeContent, []);
    }
  };

  const runTrace = async (src, inputs) => {
    setIsTracing(true);
    setShowInputModal(false);
    try {
      const res = await fetch(`${PYTHON_INTERPRETER_URL}/trace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
        body: JSON.stringify({ code: src, inputs })
      });
      const data = await res.json();
      if (data.steps && data.steps.length > 0) {
        setTraceSteps(data.steps);
        setShowVisualizer(true);
      } else {
        setRunOutput(prev => ({ ...prev, [selectedCodeId || 'new']: '⚠️ Visualizer: no executable steps found.' }));
      }
    } catch (err) {
      setRunOutput(prev => ({ ...prev, [selectedCodeId || 'new']: `⚠️ Visualizer error: ${err.message}` }));
    } finally {
      setIsTracing(false);
    }
  };

  const handleRunTurtle = async () => {
    if (!codeContent.trim()) return;
    setShowTurtlePanel(true);
    setTurtleRunning(true);
    setTurtleOutput('⏳ Loading Skulpt...');

    // Clear previous canvas
    const container = document.getElementById('turtle-canvas-classcode');
    if (container) container.innerHTML = '';

    try {
      await loadSkulpt();
      setSkulptLoaded(true);
      setTurtleOutput('🐢 Running...');

      window.Sk.configure({
        output: (text) => setTurtleOutput(prev => prev + text),
        read: (filename) => {
          if (!window.Sk.builtinFiles?.files?.[filename])
            throw "File not found: '" + filename + "'";
          return window.Sk.builtinFiles.files[filename];
        },
        __future__: window.Sk.python3,
      });

      window.Sk.TurtleGraphics = window.Sk.TurtleGraphics || {};
      window.Sk.TurtleGraphics.target = 'turtle-canvas-classcode';
      window.Sk.TurtleGraphics.width = 1200;
      window.Sk.TurtleGraphics.height = 800;
      window.Sk.TurtleGraphics.animate = true;
      window.Sk.TurtleGraphics.delay = 0; // draw at maximum speed

      await window.Sk.misceval.asyncToPromise(() =>
        window.Sk.importMainWithBody('<stdin>', false, codeContent, true)
      );
      setTurtleOutput('✅ Done!');
    } catch (err) {
      setTurtleOutput('❌ Error: ' + err.toString());
    } finally {
      setTurtleRunning(false);
    }
  };

  // Run code — auto-routes to Skulpt for turtle, server for everything else
  const handleRunCode = async (code, codeId = null) => {
    const id = codeId || code.id || 'new';
    const codeStr = typeof code === 'string' ? code : code.code;

    // Demo mode: code execution disabled (covers both server runs and in-browser turtle)
    if (isDemoMode()) {
      setRunOutput(prev => ({ ...prev, [id]: '🔒 Code execution is locked in demo mode.\n\nWith an account you can run this exact code live and see its output right here.\nThe sandbox handles print(), input(), loops, and errors.\n\n✨ Sign up to run Python!' }));
      return;
    }

    // Turtle code runs in-browser via Skulpt, not on the server
    if (/^\s*(import turtle\b|from turtle\b)/m.test(codeStr)) {
      handleRunTurtle();
      return;
    }

    activeRunIdRef.current = id;
    setRunningCodeId(id);
    setExecutionId(null);
    setIsAwaitingInput(false);
    setPromptEndIndex(0);
    serverStdoutRef.current = 0;
    setRunOutput(prev => ({ ...prev, [id]: '' }));

    try {
      const res = await fetch(`${PYTHON_INTERPRETER_URL}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
        body: JSON.stringify({ code: codeStr })
      });

      const data = await res.json();
      const stdout = data.stdout || '';
      setExecutionId(data.execution_id);
      setRunOutput(prev => ({ ...prev, [id]: stdout }));
      serverStdoutRef.current = stdout.length;

      if (data.execution_id) {
        // Code paused waiting for input()
        setIsAwaitingInput(true);
        setPromptEndIndex(stdout.length);
      } else {
        const stderr = data.stderr || '';
        if (stderr) {
          setRunOutput(prev => ({ ...prev, [id]: stdout + '\nError:\n' + stderr }));
        } else if (!stdout) {
          setRunOutput(prev => ({ ...prev, [id]: '(No output)' }));
        }
      }
    } catch (err) {
      logger.error('Error running code:', err);
      setRunOutput(prev => ({ ...prev, [id]: `Error: ${err.message}` }));
    } finally {
      setRunningCodeId(null);
    }
  };

  const handleResumeExecution = async (userInput) => {
    if (isDemoMode()) return;
    const id = activeRunIdRef.current;
    setRunningCodeId(id);
    setIsAwaitingInput(false);

    try {
      const res = await fetch(`${PYTHON_INTERPRETER_URL}/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
        body: JSON.stringify({ execution_id: executionId, input: userInput })
      });

      const data = await res.json();
      const newStdout = data.stdout || '';
      setExecutionId(data.execution_id);
      setRunOutput(prev => ({ ...prev, [id]: newStdout }));
      setPromptEndIndex(newStdout.length);

      if (data.execution_id) {
        setIsAwaitingInput(true);
      } else {
        const stderr = data.stderr || '';
        if (stderr) {
          setRunOutput(prev => ({ ...prev, [id]: newStdout + '\nError:\n' + stderr }));
        }
        setExecutionId(null);
      }
    } catch (error) {
      setRunOutput(prev => ({ ...prev, [id]: (prev[id] || '') + '\nFailed to connect to code server.' }));
      setExecutionId(null);
    } finally {
      setRunningCodeId(null);
    }
  };

  const handleTerminateCode = async () => {
    const id = activeRunIdRef.current;
    if (!executionId) {
      setIsAwaitingInput(false);
      setRunningCodeId(null);
      return;
    }
    try {
      await fetch(`${PYTHON_INTERPRETER_URL}/terminate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
        body: JSON.stringify({ execution_id: executionId })
      });
    } catch (error) {
      console.error('Failed to terminate execution:', error);
    } finally {
      setRunOutput(prev => ({ ...prev, [id]: (prev[id] || '') + '\n[Execution Terminated]' }));
      setIsAwaitingInput(false);
      setExecutionId(null);
      setRunningCodeId(null);
      serverStdoutRef.current = 0;
    }
  };

  const handleOutputKeyDown = (e) => {
    if (isAwaitingInput && e.key === 'Enter') {
      e.preventDefault();
      const userInput = e.target.value.substring(promptEndIndex);
      handleResumeExecution(userInput.replace(/\n$/, ''));
    }
  };

  // Get explanation - fetch fresh from database first, fallback to OpenAI if not available
  const handleExplain = async (code) => {
    setShowExplanationModal(true);
    setExplaining(true);
    setExplanation('');

    try {
      // First, try to fetch the latest explanation from database (in case background saved it)
      if (selectedCodeId) {
        const freshRes = await apiFetch(`${API_BASE_URL}/api/classCode/byId?id=${selectedCodeId}`);
        if (freshRes.ok) {
          const freshData = await freshRes.json();
          if (freshData.success && freshData.code && freshData.code.explanation) {
            setExplanation(freshData.code.explanation);
            setExplaining(false);
            return;
          }
        }
      }

      // If no explanation in database yet, generate via OpenAI
      const res = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: 'You are a helpful Python tutor. Explain the following code in a clear, beginner-friendly way. Break down what each part does and explain any important concepts.'
            },
            {
              role: 'user',
              content: `Please explain this Python code:\n\n\`\`\`python\n${code.code}\n\`\`\``
            }
          ],
          temperature: 0.7
        })
      });

      const data = await res.json();
      if (res.ok && data.choices && data.choices[0]) {
        const generatedExplanation = data.choices[0].message.content;
        setExplanation(generatedExplanation);

        // Save the generated explanation back to the database so it's cached for next time
        if (selectedCodeId) {
          try {
            await apiFetch(`${API_BASE_URL}/api/classCode/update`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: selectedCodeId,
                name: codeName.trim(),
                code: codeContent,
                explanation: generatedExplanation
              })
            });
            // Refresh the codes list so the cached explanation is available immediately
            await loadCodes();
          } catch (saveErr) {
            logger.error('Error saving explanation to DB:', saveErr);
          }
        }
      } else {
        setExplanation('Failed to get explanation. Please try again.');
      }
    } catch (err) {
      logger.error('Error getting explanation:', err);
      setExplanation('Error getting explanation. Please try again.');
    }
    setExplaining(false);
  };

  // Tutor Help: Send message to debugging tutor
  const handleTutorSend = async () => {
    if (!tutorInput.trim() || tutorLoading) return;

    const userMessage = tutorInput.trim();
    setTutorInput('');
    setTutorMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setTutorLoading(true);

    // Build conversation history for the API
    const conversationHistory = [
      ...tutorMessages.map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content: userMessage }
    ];

    // Get current output if available
    const currentOutput = runOutput['new'] || runOutput[selectedCodeId] || runOutput['edit'] || '';
    const outputSection = currentOutput && currentOutput !== 'Running...' 
      ? `\n\nThe code was run and produced this output:\n\`\`\`\n${currentOutput}\n\`\`\`` 
      : '';

    // Debugging tutor system prompt
    const systemPrompt = `You are an expert Python debugging tutor. Your goal is to help students find and fix bugs in their code using the Socratic method.

The student is working on this code:
\`\`\`python
${codeContent}
\`\`\`${outputSection}

General Rules:
1. Your primary focus is debugging Python code.
2. Ask them what they expect the code to do and what it is actually doing.
3. Guide them to use print statements or test different inputs to isolate the issue.
4. Give them hints to find and fix the bug themselves.
5. Work on one bug at a time.
6. Encourage them to explain their thought process.
7. Do not provide the full fixed code immediately - guide them to discover the fix.
8. If they're stuck after several attempts, give progressively more direct hints.
9. Be encouraging and supportive.
10. Keep responses concise but helpful.
11. If output is provided, analyze it to help identify the bug.`;

    try {
      const res = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            ...conversationHistory
          ],
          temperature: 0.7
        })
      });

      const data = await res.json();
      if (res.ok && data.choices && data.choices[0]) {
        const assistantMessage = data.choices[0].message.content;
        setTutorMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
      } else {
        setTutorMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
      }
    } catch (err) {
      logger.error('Error in tutor chat:', err);
      setTutorMessages(prev => [...prev, { role: 'assistant', content: 'Error connecting to tutor. Please try again.' }]);
    }
    setTutorLoading(false);

    // Scroll to bottom of chat
    setTimeout(() => {
      if (tutorChatRef.current) {
        tutorChatRef.current.scrollTop = tutorChatRef.current.scrollHeight;
      }
    }, 100);
  };

  // Start Tutor Help session
  const handleStartTutorHelp = () => {
    setShowTutorHelp(true);
    // Initial message from tutor
    if (tutorMessages.length === 0) {
      setTutorMessages([{
        role: 'assistant',
        content: `Hi! I'm your debugging tutor. I can see your code above. 🐍\n\nWhat issue are you experiencing? Tell me:\n1. What do you expect the code to do?\n2. What is it actually doing instead?`
      }]);
    }
  };

  // Student: Save memo to localStorage
  const handleSaveMemo = (codeId, memoText) => {
    localStorage.setItem(`memo_classCode_${codeId}`, memoText);
    setMemos(prev => ({ ...prev, [codeId]: memoText }));
  };

  // Format timestamp for display (already in EST from backend)
  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Styles
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  };

  const modalContentStyle = {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    padding: '24px',
    width: '95%',
    maxWidth: isAdmin ? '1600px' : '1400px',
    maxHeight: '98vh',
    overflow: 'auto',
    border: '0.5px solid #d1fae5'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '1px solid #333',
    paddingBottom: '15px'
  };

  const buttonStyle = {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#238636',
    color: '#ffffff'
  };

  const adminButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#1f6feb',
    color: '#ffffff'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#da3633',
    color: '#ffffff'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#30363d',
    color: '#ffffff'
  };

  const codeBlockStyle = {
    backgroundColor: '#161b22',
    border: '0.5px solid #d1fae5',
    borderRadius: '6px',
    padding: '12px',
    fontFamily: 'Consolas, Monaco, monospace',
    fontSize: '13px',
    color: '#ffffff',
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
    maxHeight: '200px'
  };

  return (
    <>
    <div style={modalOverlayStyle} onClick={handleClose}>
      <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, color: '#ffffff' }}>
            📁 Class Codes {isAdmin ? '(Admin)' : ''}
          </h2>
          <button
            onClick={handleClose}
            style={{
              ...buttonStyle,
              backgroundColor: 'transparent',
              color: '#ffffff',
              fontSize: '20px',
              padding: '4px 10px'
            }}
            onMouseOver={(e) => e.target.style.color = '#f85149'}
            onMouseOut={(e) => e.target.style.color = '#ffffff'}
          >
            ✕
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            backgroundColor: '#5c1e1e',
            color: '#f85149',
            padding: '10px 15px',
            borderRadius: '6px',
            marginBottom: '15px'
          }}>
            {error}
          </div>
        )}

        {/* PyCharm-style IDE layout - for both Admin and Student */}
        {isDemoMode() ? (
          <div style={{
            flex: 1,
            backgroundColor: '#161b22',
            borderRadius: '8px',
            border: '0.5px solid #d1fae5',
            padding: '40px 36px',
            overflowY: 'auto',
            color: '#fffacd',
            whiteSpace: 'pre-wrap',
            lineHeight: '1.8',
            fontSize: '14px'
          }}>
            {`🔒 Class Codes — Locked in Demo Mode

With a real account this panel is a shared IDE-style workspace for your class.

What instructors can do:
  📁  Organize code files into folders (e.g. "Week 3 / fibonacci.py")
  💾  Save Python snippets with auto-generated AI explanations
  📤  Share any file with the class instantly — students see it in their Explorer
  ⬇️  Download all class files as a ZIP

What students can do:
  👀  Browse and open any file the instructor has shared
  ▶️  Run the code live in the sandbox with one click
  💬  Ask the tutor "explain this code" on any file
  💾  Save their own code files to a personal "My Files" section

Example class file tree:

  📂 EXPLORER
  ├── 📁 Class Files (shared by instructor)
  │   ├── 📄 week1/hello_world.py
  │   ├── 📄 week2/for_loop_example.py
  │   └── 📄 week3/fibonacci.py
  └── 📁 My Files (your own saves)
      └── 📄 my_practice.py

The instructor's files appear automatically in every student's panel —
no copy-pasting, no email attachments.

✨ Sign up to access your class workspace!`}
          </div>
        ) : (
        <div style={{ display: 'flex', gap: '0px', height: 'calc(98vh - 120px)' }}>
          
          {/* LEFT PANEL - File Browser / Code Selector */}
          <div style={{
            width: '280px',
            minWidth: '280px',
            backgroundColor: '#161b22',
            borderRadius: '8px 0 0 8px',
            border: '0.5px solid #d1fae5',
            borderRight: '0.5px solid #d1fae5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Left panel header */}
            <div style={{
              padding: '12px 15px',
              borderBottom: '0.5px solid #d1fae5',
              backgroundColor: '#0d1117'
            }}>
              <h4 style={{ margin: 0, color: '#fbbf24', fontSize: '13px', letterSpacing: '0.5px' }}>
                📂 EXPLORER
              </h4>
              <div style={{ marginTop: '8px', color: '#fbbf24', fontSize: '11px' }}>
                {classSection} • {semester} • {university}
              </div>
            </div>

            {/* Scrollable file sections */}
            <div ref={fileBrowserRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* ===== SECTION 1: CLASS FILES (Admin Work) ===== */}
              <div style={{
                height: !isAdmin && classFilesHeight ? `${classFilesHeight}px` : (isAdmin ? '100%' : '50%'),
                minHeight: '60px',
                overflowY: 'auto',
                flexShrink: 0
              }}>
                {/* Section header - collapsible */}
                <div
                  onClick={() => setClassFilesCollapsed(prev => !prev)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#0d1117',
                    borderBottom: '1px solid #21262d',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    userSelect: 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#6e7681', fontSize: '10px' }}>{classFilesCollapsed ? '▶' : '▼'}</span>
                    <span style={{ color: '#58a6ff', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>
                      📂 CLASS FILES
                    </span>
                  </div>
                  <span style={{ color: '#6e7681', fontSize: '10px' }}>
                    {codeNames.filter(cn => cn.createdBy === 'admin').length}
                  </span>
                </div>

                {!classFilesCollapsed && (() => {
                  const classFiles = codeNames.filter(cn => cn.createdBy === 'admin');
                  const { rootFiles, folders } = buildFolderTree(classFiles, 'class');
                  const folderNames = Object.keys(folders).sort();

                  return (
                  <div>
                    {/* Admin buttons: New Folder + New File */}
                    {isAdmin && (
                      <div style={{ padding: '8px 10px', display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => { setCreatingFolderIn('class'); setNewFolderName(''); }}
                          style={{
                            flex: 1, padding: '6px 10px', borderRadius: '4px',
                            backgroundColor: creatingFolderIn === 'class' ? '#1f6feb' : '#21262d',
                            color: creatingFolderIn === 'class' ? 'white' : '#ffffff',
                            border: '0.5px solid #30363d', cursor: 'pointer',
                            fontSize: '11px', fontWeight: '600', textAlign: 'center'
                          }}
                        >📁+ Folder</button>
                      </div>
                    )}

                    {/* New folder name input */}
                    {isAdmin && creatingFolderIn === 'class' && (
                      <div style={{ padding: '0 10px 8px', display: 'flex', gap: '4px' }}>
                        <input type="text" value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value.replace(/\//g, ''))}
                          placeholder="Folder name..." autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newFolderName.trim()) {
                              const fn = newFolderName.trim();
                              saveEmptyFolders({ ...emptyFolders, class: [...(emptyFolders.class || []).filter(f => f !== fn), fn] });
                              setCreatingFolderIn(null); setNewFolderName('');
                              setCollapsedFolders(prev => ({ ...prev, [`class-${fn}`]: true }));
                            } else if (e.key === 'Escape') { setCreatingFolderIn(null); setNewFolderName(''); }
                          }}
                          style={{ flex: 1, padding: '5px 8px', borderRadius: '4px', backgroundColor: '#21262d', color: '#ffffff', border: '0.5px solid #1f6feb', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                        <button onClick={() => {
                          if (newFolderName.trim()) {
                            const fn = newFolderName.trim();
                            saveEmptyFolders({ ...emptyFolders, class: [...(emptyFolders.class || []).filter(f => f !== fn), fn] });
                            setCreatingFolderIn(null); setNewFolderName('');
                            setCollapsedFolders(prev => ({ ...prev, [`class-${fn}`]: false }));
                          }
                        }} style={{ padding: '5px 8px', borderRadius: '4px', backgroundColor: '#238636', color: '#ffffff', border: 'none', cursor: 'pointer', fontSize: '11px' }}>✓</button>
                      </div>
                    )}

                    {/* File name input inside a folder (from context menu right-click → New File) */}
                    {isAdmin && editMode === 'new' && selectedFileSource === 'class' && activeFolder && (
                      <div style={{ padding: '0 10px 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ padding: '6px 6px 6px 8px', borderRadius: '4px 0 0 4px', backgroundColor: '#1f6feb33', color: '#fbbf24', border: '0.5px solid #1f6feb', borderRight: 'none', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>📁 {activeFolder}/</span>
                          <input type="text"
                            value={codeName.startsWith(activeFolder + '/') ? codeName.substring(activeFolder.length + 1) : codeName}
                            onChange={(e) => setCodeName(activeFolder + '/' + e.target.value.replace(/\//g, ''))}
                            placeholder="file name..." autoFocus
                            style={{ flex: 1, padding: '6px 8px', borderRadius: '0 4px 4px 0', backgroundColor: '#21262d', color: '#ffffff', border: '0.5px solid #1f6feb', fontSize: '12px', boxSizing: 'border-box', outline: 'none' }}
                          />
                        </div>
                      </div>
                    )}

                    {loading ? (
                      <div style={{ padding: '10px', color: '#6e7681', fontSize: '11px', textAlign: 'center' }}>Loading...</div>
                    ) : (
                      <>
                        {/* Folders */}
                        {folderNames.map(folder => (
                          <div key={`folder-class-${folder}`}>
                            <div
                              onClick={() => toggleFolder(`class-${folder}`)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setContextMenu({ x: e.clientX, y: e.clientY, type: 'folder', name: folder, section: 'class' });
                              }}
                              style={{
                                padding: '6px 15px 6px 18px', cursor: 'pointer', color: '#fbbf24',
                                fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px', userSelect: 'none',
                                backgroundColor: activeFolder === folder && selectedFileSource === 'class' ? '#1f6feb15' : 'transparent'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#21262d'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = activeFolder === folder && selectedFileSource === 'class' ? '#1f6feb15' : 'transparent'; }}
                            >
                              <span style={{ fontSize: '10px', color: '#6e7681' }}>{isFolderOpen(`class-${folder}`) ? '▼' : '▶'}</span>
                              <span>📁</span>
                              <span style={{ fontWeight: '600' }}>{folder}</span>
                              <span style={{ color: '#6e7681', fontSize: '10px', marginLeft: 'auto' }}>{folders[folder].length}</span>
                            </div>
                            {isFolderOpen(`class-${folder}`) && folders[folder].map(f => (
                              <div key={f.id}
                                onClick={() => { handleSelectCode(f.id); setSelectedFileSource('class'); setMyFileEditMode(null); setActiveFolder(null); }}
                                onContextMenu={(e) => {
                                  if (!isAdmin) return;
                                  e.preventDefault();
                                  setContextMenu({ x: e.clientX, y: e.clientY, type: 'file', name: f.displayName, id: f.id, section: 'class' });
                                }}
                                style={{
                                  padding: '5px 15px 5px 40px', cursor: 'pointer',
                                  backgroundColor: selectedCodeId === f.id ? '#1f6feb22' : 'transparent',
                                  borderLeft: selectedCodeId === f.id ? '3px solid #1f6feb' : '3px solid transparent',
                                  color: selectedCodeId === f.id ? '#58a6ff' : '#ffffff',
                                  fontSize: '12px', transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: '5px'
                                }}
                                onMouseEnter={(e) => { if (selectedCodeId !== f.id) e.currentTarget.style.backgroundColor = '#21262d'; }}
                                onMouseLeave={(e) => { if (selectedCodeId !== f.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                              >
                                <span style={{ fontSize: '12px' }}>🐍</span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {f.displayName.endsWith('.py') ? f.displayName : f.displayName + '.py'}
                                </span>
                              </div>
                            ))}
                          </div>
                        ))}
                        {/* Root-level files */}
                        {rootFiles.map(f => (
                          <div key={f.id}
                            onClick={() => { handleSelectCode(f.id); setSelectedFileSource('class'); setMyFileEditMode(null); setActiveFolder(null); }}
                            onContextMenu={(e) => {
                              if (!isAdmin) return;
                              e.preventDefault();
                              setContextMenu({ x: e.clientX, y: e.clientY, type: 'file', name: f.displayName, id: f.id, section: 'class' });
                            }}
                            style={{
                              padding: '6px 15px 6px 24px', cursor: 'pointer',
                              backgroundColor: selectedCodeId === f.id ? '#1f6feb22' : 'transparent',
                              borderLeft: selectedCodeId === f.id ? '3px solid #1f6feb' : '3px solid transparent',
                              color: selectedCodeId === f.id ? '#58a6ff' : '#ffffff',
                              fontSize: '13px', transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: '6px'
                            }}
                            onMouseEnter={(e) => { if (selectedCodeId !== f.id) e.currentTarget.style.backgroundColor = '#21262d'; }}
                            onMouseLeave={(e) => { if (selectedCodeId !== f.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <span style={{ fontSize: '13px' }}>🐍</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {f.displayName.endsWith('.py') ? f.displayName : f.displayName + '.py'}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                    {!loading && classFiles.length === 0 && folderNames.length === 0 && (
                      <div style={{ padding: '10px 15px', color: '#6e7681', fontSize: '11px' }}>No class files yet</div>
                    )}
                  </div>
                  );
                })()}
              </div>

              {/* Draggable divider between CLASS FILES and MY FILES */}
              {!isAdmin && (
                <div
                  style={{
                    height: '6px',
                    minHeight: '6px',
                    backgroundColor: isDraggingSectionDivider.current ? '#1f6feb' : '#0d1117',
                    borderTop: '0.5px solid #d1fae5',
                    borderBottom: '0.5px solid #d1fae5',
                    cursor: 'row-resize',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1f6feb'; }}
                  onMouseLeave={(e) => { if (!isDraggingSectionDivider.current) e.currentTarget.style.backgroundColor = '#0d1117'; }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isDraggingSectionDivider.current = true;
                    const startY = e.clientY;
                    const container = fileBrowserRef.current;
                    const containerRect = container ? container.getBoundingClientRect() : { height: 400, top: 0 };
                    const startHeight = classFilesHeight || (containerRect.height * 0.5);

                    const onMouseMove = (ev) => {
                      const delta = ev.clientY - startY;
                      const newHeight = Math.max(60, Math.min(containerRect.height - 80, startHeight + delta));
                      setClassFilesHeight(newHeight);
                    };
                    const onMouseUp = () => {
                      isDraggingSectionDivider.current = false;
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                      document.body.style.userSelect = '';
                      document.body.style.cursor = '';
                    };
                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                    document.body.style.userSelect = 'none';
                    document.body.style.cursor = 'row-resize';
                  }}
                >
                  <div style={{
                    width: '36px',
                    height: '2px',
                    backgroundColor: '#484f58',
                    borderRadius: '1px'
                  }} />
                </div>
              )}

              {/* ===== SECTION 2: MY FILES (Student's Own Work) ===== */}
              {!isAdmin && (
                <div style={{ flex: 1, overflowY: 'auto', minHeight: '60px' }}>
                  {/* Section header - collapsible */}
                  <div
                    onClick={() => setMyFilesCollapsed(prev => !prev)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#0d1117',
                      borderTop: '0.5px solid #d1fae5',
                      borderBottom: '1px solid #21262d',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ color: '#6e7681', fontSize: '10px' }}>{myFilesCollapsed ? '▶' : '▼'}</span>
                      <span style={{ color: '#3fb950', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>
                        📁 MY FILES
                      </span>
                    </div>
                    <span style={{ color: '#6e7681', fontSize: '10px' }}>
                      {codeNames.filter(cn => cn.createdBy === username).length}
                    </span>
                  </div>

                  {!myFilesCollapsed && (() => {
                    const myFiles = codeNames.filter(cn => cn.createdBy === username);
                    const { rootFiles, folders } = buildFolderTree(myFiles, 'my');
                    const folderNames = Object.keys(folders).sort();

                    return (
                    <div>
                      {/* Student buttons: New Folder + New File */}
                      <div style={{ padding: '8px 10px', display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => { setCreatingFolderIn('my'); setNewFolderName(''); }}
                          style={{
                            flex: 1, padding: '6px 10px', borderRadius: '4px',
                            backgroundColor: creatingFolderIn === 'my' ? '#1f6feb' : '#21262d',
                            color: creatingFolderIn === 'my' ? 'white' : '#ffffff',
                            border: '0.5px solid #30363d', cursor: 'pointer',
                            fontSize: '11px', fontWeight: '600', textAlign: 'center'
                          }}
                        >📁+ Folder</button>
                      </div>

                      {/* New folder name input */}
                      {creatingFolderIn === 'my' && (
                        <div style={{ padding: '0 10px 8px', display: 'flex', gap: '4px' }}>
                          <input type="text" value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value.replace(/\//g, ''))}
                            placeholder="Folder name..." autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newFolderName.trim()) {
                                const fn = newFolderName.trim();
                                saveEmptyFolders({ ...emptyFolders, my: [...(emptyFolders.my || []).filter(f => f !== fn), fn] });
                                setCreatingFolderIn(null); setNewFolderName('');
                                setCollapsedFolders(prev => ({ ...prev, [`my-${fn}`]: true }));
                              } else if (e.key === 'Escape') { setCreatingFolderIn(null); setNewFolderName(''); }
                            }}
                            style={{ flex: 1, padding: '5px 8px', borderRadius: '4px', backgroundColor: '#21262d', color: '#ffffff', border: '0.5px solid #1f6feb', fontSize: '12px', boxSizing: 'border-box' }}
                          />
                          <button onClick={() => {
                            if (newFolderName.trim()) {
                              const fn = newFolderName.trim();
                              saveEmptyFolders({ ...emptyFolders, my: [...(emptyFolders.my || []).filter(f => f !== fn), fn] });
                              setCreatingFolderIn(null); setNewFolderName('');
                              setCollapsedFolders(prev => ({ ...prev, [`my-${fn}`]: true }));
                            }
                          }} style={{ padding: '5px 8px', borderRadius: '4px', backgroundColor: '#238636', color: '#ffffff', border: 'none', cursor: 'pointer', fontSize: '11px' }}>✓</button>
                        </div>
                      )}

                      {/* File name input inside a folder (from context menu right-click → New File) */}
                      {myFileEditMode === 'new' && activeFolder && selectedFileSource === 'my' && (
                        <div style={{ padding: '0 10px 8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ padding: '6px 6px 6px 8px', borderRadius: '4px 0 0 4px', backgroundColor: '#3fb95033', color: '#fbbf24', border: '0.5px solid #3fb950', borderRight: 'none', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>📁 {activeFolder}/</span>
                            <input type="text"
                              value={codeName.startsWith(activeFolder + '/') ? codeName.substring(activeFolder.length + 1) : myFileName}
                              onChange={(e) => { const val = activeFolder + '/' + e.target.value.replace(/\//g, ''); setCodeName(val); setMyFileName(val); }}
                              placeholder="file name..." autoFocus
                              style={{ flex: 1, padding: '6px 8px', borderRadius: '0 4px 4px 0', backgroundColor: '#21262d', color: '#ffffff', border: '0.5px solid #3fb950', fontSize: '12px', boxSizing: 'border-box', outline: 'none' }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Folders */}
                      {folderNames.map(folder => (
                        <div key={`folder-my-${folder}`}>
                          <div
                            onClick={() => toggleFolder(`my-${folder}`)}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setContextMenu({ x: e.clientX, y: e.clientY, type: 'folder', name: folder, section: 'my' });
                            }}
                            style={{
                              padding: '6px 15px 6px 18px', cursor: 'pointer', color: '#fbbf24',
                              fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px', userSelect: 'none',
                              backgroundColor: activeFolder === folder && selectedFileSource === 'my' ? '#3fb95015' : 'transparent'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#21262d'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = activeFolder === folder && selectedFileSource === 'my' ? '#3fb95015' : 'transparent'; }}
                          >
                            <span style={{ fontSize: '10px', color: '#6e7681' }}>{isFolderOpen(`my-${folder}`) ? '▼' : '▶'}</span>
                            <span>📁</span>
                            <span style={{ fontWeight: '600' }}>{folder}</span>
                            <span style={{ color: '#6e7681', fontSize: '10px', marginLeft: 'auto' }}>{folders[folder].length}</span>
                          </div>
                          {isFolderOpen(`my-${folder}`) && folders[folder].map(f => (
                            <div key={f.id}
                              onClick={() => { handleSelectCode(f.id); setSelectedFileSource('my'); setMyFileEditMode(null); setActiveFolder(null); }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setContextMenu({ x: e.clientX, y: e.clientY, type: 'file', name: f.displayName, id: f.id, section: 'my' });
                              }}
                              style={{
                                padding: '5px 15px 5px 40px', cursor: 'pointer',
                                backgroundColor: selectedCodeId === f.id ? '#3fb95022' : 'transparent',
                                borderLeft: selectedCodeId === f.id ? '3px solid #3fb950' : '3px solid transparent',
                                color: selectedCodeId === f.id ? '#3fb950' : '#ffffff',
                                fontSize: '12px', transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: '5px'
                              }}
                              onMouseEnter={(e) => { if (selectedCodeId !== f.id) e.currentTarget.style.backgroundColor = '#21262d'; }}
                              onMouseLeave={(e) => { if (selectedCodeId !== f.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              <span style={{ fontSize: '12px' }}>🐍</span>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {f.displayName.endsWith('.py') ? f.displayName : f.displayName + '.py'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                      {/* Root-level files */}
                      {rootFiles.map(f => (
                        <div key={f.id}
                          onClick={() => { handleSelectCode(f.id); setSelectedFileSource('my'); setMyFileEditMode(null); setActiveFolder(null); }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setContextMenu({ x: e.clientX, y: e.clientY, type: 'file', name: f.displayName, id: f.id, section: 'my' });
                          }}
                          style={{
                            padding: '6px 15px 6px 24px', cursor: 'pointer',
                            backgroundColor: selectedCodeId === f.id ? '#3fb95022' : 'transparent',
                            borderLeft: selectedCodeId === f.id ? '3px solid #3fb950' : '3px solid transparent',
                            color: selectedCodeId === f.id ? '#3fb950' : '#ffffff',
                            fontSize: '13px', transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: '6px'
                          }}
                          onMouseEnter={(e) => { if (selectedCodeId !== f.id) e.currentTarget.style.backgroundColor = '#21262d'; }}
                          onMouseLeave={(e) => { if (selectedCodeId !== f.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <span style={{ fontSize: '13px' }}>🐍</span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.displayName.endsWith('.py') ? f.displayName : f.displayName + '.py'}
                          </span>
                        </div>
                      ))}
                      {myFiles.length === 0 && folderNames.length === 0 && myFileEditMode !== 'new' && (
                        <div style={{ padding: '10px 15px', color: '#6e7681', fontSize: '11px' }}>No files yet — create one!</div>
                      )}
                    </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* File count */}
            <div style={{
              padding: '10px 15px',
              borderTop: '0.5px solid #d1fae5',
              color: '#fbbf24',
              fontSize: '11px',
              backgroundColor: '#0d1117'
            }}>
              {codeNames.length} file{codeNames.length !== 1 ? 's' : ''} total
            </div>
          </div>

          {/* RIGHT PANEL - Code Editor + Output */}
          <div style={{
            flex: 1,
            backgroundColor: '#1e1e1e',
            borderRadius: '0 8px 8px 0',
            border: '0.5px solid #d1fae5',
            borderLeft: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Editor tab bar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 10px',
              backgroundColor: '#0d1117',
              borderBottom: '0.5px solid #d1fae5',
              minHeight: '38px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: '#1e1e1e',
                borderRadius: '6px 6px 0 0',
                marginTop: '4px',
                borderTop: '2px solid #1f6feb'
              }}>
                <span style={{ fontSize: '13px' }}>🐍</span>
                <span style={{ color: '#ffffff', fontSize: '13px' }}>
                  {(() => {
                    const displayName = codeName && codeName.includes('/') ? codeName.substring(codeName.lastIndexOf('/') + 1) : codeName;
                    const folderPrefix = codeName && codeName.includes('/') ? codeName.substring(0, codeName.lastIndexOf('/')) : null;
                    if (editMode === 'new' && !displayName) return 'untitled.py';
                    if (!codeName) return 'Select a file...';
                    const pyName = displayName ? (displayName.endsWith('.py') ? displayName : displayName + '.py') : 'untitled.py';
                    return folderPrefix ? `${folderPrefix}/${pyName}` : pyName;
                  })()}
                </span>
              </div>

              {/* Action buttons in toolbar */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Editor mode toggle */}
                <button
                  onClick={() => setUseMonaco(prev => !prev)}
                  title={useMonaco ? 'Switch to Simple Editor' : 'Switch to Monaco Editor (IntelliSense)'}
                  style={{
                    backgroundColor: useMonaco ? '#1f6feb' : '#6e7681',
                    color: '#ffffff',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '11px',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {useMonaco ? '🧠 Monaco Editor' : '📝 Simple Editor'}
                </button>
                {/* Theme toggle */}
                <button
                  onClick={() => setEditorTheme(prev => prev === 'vs-dark' ? 'vs' : 'vs-dark')}
                  title={editorTheme === 'vs-dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
                  style={{
                    backgroundColor: editorTheme === 'vs-dark' ? '#21262d' : '#f0f0f0',
                    color: editorTheme === 'vs-dark' ? '#ffffff' : '#1e1e1e',
                    border: '1px solid #30363d',
                    padding: '5px 10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '11px',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {editorTheme === 'vs-dark' ? '☀️ Light' : '🌙 Dark'}
                </button>
                <div style={{ width: '1px', height: '20px', backgroundColor: '#30363d' }} />
                {/* Run button — always visible when there's code */}
                {(selectedCodeId || editMode === 'new' || myFileEditMode === 'new') && (
                  <>
                  <button
                    onClick={() => handleRunCode(codeContent, selectedCodeId || 'new')}
                    disabled={!codeContent.trim() || runningCodeId !== null || isAwaitingInput || turtleRunning}
                    style={{
                      backgroundColor: '#238636',
                      color: '#ffffff',
                      border: 'none',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      cursor: (!codeContent.trim() || runningCodeId !== null || isAwaitingInput || turtleRunning) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '12px',
                      opacity: (!codeContent.trim() || runningCodeId !== null || isAwaitingInput || turtleRunning) ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                    title="Run code — auto-detects turtle and runs in browser"
                  >
                    {turtleRunning ? '🐢 Running...' : runningCodeId ? '⏳ Running...' : '▶ Run'}
                  </button>
                  {(runningCodeId || executionId) && (
                    <button
                      onClick={handleTerminateCode}
                      style={{
                        backgroundColor: '#dc2626',
                        color: '#ffffff',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '12px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      ⏹️ Stop
                    </button>
                  )}
                  <button
                    onClick={handleVisualize}
                    disabled={isTracing || !codeContent.trim()}
                    style={{
                      backgroundColor: isTracing ? '#21262d' : '#1d3a5f',
                      color: isTracing ? '#4b5563' : '#79c0ff',
                      border: isTracing ? '1px solid #388bfd55' : '1px solid rgba(255, 240, 150, 0.75)',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      cursor: (isTracing || !codeContent.trim()) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '12px',
                      opacity: !codeContent.trim() ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                    title="Step through code line by line"
                  >
                    {isTracing ? '⏳ Tracing...' : '🔍 Visualize'}
                  </button>
                  </>
                )}
                {/* Download — always visible when there's code */}
                {(selectedCodeId || editMode === 'new' || myFileEditMode === 'new') && (
                  <button
                    onClick={handleDownloadFile}
                    disabled={!codeContent.trim()}
                    style={{
                      backgroundColor: '#0d6efd',
                      color: '#ffffff',
                      border: 'none',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      cursor: !codeContent.trim() ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '12px',
                      opacity: !codeContent.trim() ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    📥 Download
                  </button>
                )}
                {/* Save — admin new class file */}
                {isAdmin && editMode === 'new' && selectedFileSource === 'class' && (
                  <button
                    onClick={handleSaveNew}
                    disabled={saving}
                    style={{ ...adminButtonStyle, padding: '6px 14px', fontSize: '12px', opacity: saving ? 0.6 : 1 }}
                  >
                    {saving ? 'Saving...' : '💾 Save'}
                  </button>
                )}
                {/* Save/Delete — admin editing existing class file */}
                {isAdmin && selectedCodeId && selectedFileSource === 'class' && editMode === 'edit' && (
                  <>
                    <button
                      onClick={handleUpdate}
                      disabled={saving}
                      style={{ ...adminButtonStyle, padding: '6px 14px', fontSize: '12px', opacity: saving ? 0.6 : 1 }}
                    >
                      {saving ? 'Saving...' : '💾 Save'}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={saving}
                      style={{ ...dangerButtonStyle, padding: '6px 14px', fontSize: '12px', opacity: saving ? 0.6 : 1 }}
                    >
                      {saving ? '...' : '🗑️ Delete'}
                    </button>
                  </>
                )}
                {/* Save — student new file in My Files */}
                {!isAdmin && myFileEditMode === 'new' && selectedFileSource === 'my' && (
                  <button
                    onClick={handleSaveNew}
                    disabled={saving}
                    style={{ ...primaryButtonStyle, padding: '6px 14px', fontSize: '12px', opacity: saving ? 0.6 : 1 }}
                  >
                    {saving ? 'Saving...' : '💾 Save'}
                  </button>
                )}
                {/* Save/Delete — student editing own existing file */}
                {!isAdmin && selectedCodeId && selectedFileSource === 'my' && (
                  <>
                    <button
                      onClick={handleUpdate}
                      disabled={saving}
                      style={{ ...primaryButtonStyle, padding: '6px 14px', fontSize: '12px', opacity: saving ? 0.6 : 1 }}
                    >
                      {saving ? 'Saving...' : '💾 Save'}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={saving}
                      style={{ ...dangerButtonStyle, padding: '6px 14px', fontSize: '12px', opacity: saving ? 0.6 : 1 }}
                    >
                      {saving ? '...' : '🗑️ Delete'}
                    </button>
                  </>
                )}
                {/* Explain — always available when there's code */}
                {(selectedCodeId || editMode === 'new' || myFileEditMode === 'new') && (
                  <button
                    onClick={() => {
                      const selectedCode = codes.find(c => c.id === selectedCodeId);
                      handleExplain({ code: codeContent, explanation: selectedCode?.explanation || null });
                    }}
                    disabled={!codeContent.trim() || explaining}
                    style={{
                      backgroundColor: '#8b5cf6',
                      color: '#ffffff',
                      border: 'none',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      cursor: (!codeContent.trim() || explaining) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '12px',
                      opacity: (!codeContent.trim() || explaining) ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {explaining ? '⏳ Explaining...' : '💡 Explain'}
                  </button>
                )}
                {/* Tutor Help — only available for MY FILES (not admin class files) */}
                {(selectedFileSource === 'my' || myFileEditMode === 'new') && (selectedCodeId || myFileEditMode === 'new') && (
                  <button
                    onClick={handleStartTutorHelp}
                    disabled={!codeContent.trim()}
                    style={{
                      backgroundColor: showTutorHelp ? '#f59e0b' : '#166534',
                      color: showTutorHelp ? '#000000' : '#ffffff',
                      border: 'none',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      cursor: !codeContent.trim() ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '12px',
                      opacity: !codeContent.trim() ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {showTutorHelp ? '🎓 Tutor Active' : '🎓 Tutor Help'}
                  </button>
                )}
              </div>
            </div>

            {/* Code editor area */}
            <div ref={editorOutputContainerRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Editor container */}
              <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: '80px' }}>
                {!selectedCodeId && !(editMode === 'new') && !(myFileEditMode === 'new') ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#484f58',
                    fontSize: '14px'
                  }}>
                    ← Select a file to view
                  </div>
                ) : useMonaco ? (
                  <Editor
                    height="100%"
                    defaultLanguage="python"
                    language="python"
                    theme={editorTheme}
                    value={codeContent}
                    onChange={(value) => setCodeContent(value || '')}
                    onMount={handleEditorDidMount}
                    options={{
                      readOnly: false,
                      fontSize: 24,
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      lineHeight: 1.6,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      automaticLayout: true,
                      suggestOnTriggerCharacters: true,
                      quickSuggestions: { other: true, comments: false, strings: false },
                      wordBasedSuggestions: 'off',
                      tabSize: 4,
                      renderLineHighlight: 'line',
                      cursorBlinking: 'smooth',
                      cursorSmoothCaretAnimation: 'on',
                      padding: { top: 15 },
                      suggest: { showWords: false }
                    }}
                  />
                ) : (
                  /* Simple textarea editor with line numbers */
                  <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
                    {/* Line numbers */}
                    <div style={{
                      width: '50px',
                      backgroundColor: editorTheme === 'vs-dark' ? '#1e1e1e' : '#f3f3f3',
                      borderRight: '1px solid #30363d',
                      padding: '15px 0',
                      overflow: 'hidden',
                      userSelect: 'none',
                      textAlign: 'right',
                      flexShrink: 0
                    }}>
                      {(codeContent || '').split('\n').map((_, i) => (
                        <div key={i} style={{
                          color: '#6e7681',
                          fontSize: '24px',
                          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                          lineHeight: '1.6',
                          paddingRight: '12px',
                          height: `${24 * 1.6}px`
                        }}>
                          {i + 1}
                        </div>
                      ))}
                    </div>
                    {/* Textarea */}
                    <textarea
                      ref={textareaRef}
                      value={codeContent}
                      onChange={(e) => setCodeContent(e.target.value)}
                      readOnly={false}
                      spellCheck={false}
                      style={{
                        flex: 1,
                        backgroundColor: editorTheme === 'vs-dark' ? '#1e1e1e' : '#ffffff',
                        color: editorTheme === 'vs-dark' ? '#ffffff' : '#1e1e1e',
                        border: 'none',
                        outline: 'none',
                        resize: 'none',
                        padding: '15px 16px',
                        fontSize: '24px',
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        lineHeight: '1.6',
                        tabSize: 4,
                        whiteSpace: 'pre',
                        overflowX: 'auto',
                        overflowY: 'auto'
                      }}
                      onKeyDown={(e) => {
                        // Support Tab key for indentation
                        if (e.key === 'Tab') {
                          e.preventDefault();
                          const start = e.target.selectionStart;
                          const end = e.target.selectionEnd;
                          const newValue = codeContent.substring(0, start) + '    ' + codeContent.substring(end);
                          setCodeContent(newValue);
                          setTimeout(() => {
                            e.target.selectionStart = e.target.selectionEnd = start + 4;
                          }, 0);
                        }
                      }}
                      onScroll={(e) => {
                        // Sync line numbers scroll with textarea
                        const lineNumDiv = e.target.previousSibling;
                        if (lineNumDiv) lineNumDiv.scrollTop = e.target.scrollTop;
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Draggable divider + Output panel (below code editor) */}
              {(runOutput['new'] || runOutput[selectedCodeId] || runOutput['edit'] || runningCodeId || isAwaitingInput) && (
                <>
                {/* Draggable divider */}
                <div
                  style={{
                    height: '6px',
                    backgroundColor: isDraggingDivider.current ? '#1f6feb' : '#21262d',
                    cursor: 'row-resize',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    borderTop: '0.5px solid #d1fae5',
                    borderBottom: '0.5px solid #30363d',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1f6feb'; }}
                  onMouseLeave={(e) => { if (!isDraggingDivider.current) e.currentTarget.style.backgroundColor = '#21262d'; }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isDraggingDivider.current = true;
                    const startY = e.clientY;
                    const startHeight = outputHeight;
                    const container = editorOutputContainerRef.current;
                    const containerHeight = container ? container.getBoundingClientRect().height : 600;

                    const onMouseMove = (ev) => {
                      const delta = startY - ev.clientY;
                      const newHeight = Math.max(60, Math.min(containerHeight - 120, startHeight + delta));
                      setOutputHeight(newHeight);
                    };
                    const onMouseUp = () => {
                      isDraggingDivider.current = false;
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                      document.body.style.userSelect = '';
                      document.body.style.cursor = '';
                    };
                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                    document.body.style.userSelect = 'none';
                    document.body.style.cursor = 'row-resize';
                  }}
                >
                  <div style={{
                    width: '36px',
                    height: '2px',
                    backgroundColor: '#484f58',
                    borderRadius: '1px'
                  }} />
                </div>
                <div style={{
                  backgroundColor: '#0d1117',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  height: `${outputHeight}px`,
                  flexShrink: 0
                }}>
                  {/* Output header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 12px',
                    borderBottom: '1px solid #21262d',
                    backgroundColor: '#161b22',
                    flexShrink: 0
                  }}>
                    <span style={{ color: isAwaitingInput ? '#4ade80' : '#fbbf24', fontSize: '12px', fontWeight: '600' }}>
                      {isAwaitingInput ? '⌨️ AWAITING INPUT — type and press Enter' : '💻 OUTPUT'}
                    </span>
                    <button
                      onClick={() => {
                        setRunOutput({});
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#fbbf24',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '2px 6px'
                      }}
                      onMouseOver={(e) => e.target.style.color = '#f85149'}
                      onMouseOut={(e) => e.target.style.color = '#fbbf24'}
                    >
                      ✕
                    </button>
                  </div>
                  {/* Output textarea - supports input() interaction */}
                  <textarea
                    readOnly={!isAwaitingInput}
                    value={runOutput['new'] || runOutput[selectedCodeId] || runOutput['edit'] || ''}
                    onChange={(e) => {
                      if (isAwaitingInput) {
                        const id = activeRunIdRef.current;
                        setRunOutput(prev => ({ ...prev, [id]: e.target.value }));
                      }
                    }}
                    onKeyDown={handleOutputKeyDown}
                    placeholder={isAwaitingInput ? 'Type your input and press Enter...' : ''}
                    style={{
                      flex: 1,
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: isAwaitingInput ? '#0f1f0f' : '#0d1117',
                      color: isAwaitingInput ? '#4ade80' : '#ffffff',
                      border: isAwaitingInput ? '1px solid #22c55e' : 'none',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      resize: 'none',
                      outline: 'none',
                      boxSizing: 'border-box',
                      textAlign: 'left'
                    }}
                  />
                </div>
                </>
              )}

              {/* Turtle canvas — fixed overlay so it gets full space */}
              {showTurtlePanel && (
                <div style={{
                  position: 'fixed',
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.75)',
                  zIndex: 9999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                  onClick={(e) => {
                    // Close on backdrop click
                    if (e.target === e.currentTarget) {
                      setShowTurtlePanel(false);
                      setTurtleOutput('');
                      const c = document.getElementById('turtle-canvas-classcode');
                      if (c) c.innerHTML = '';
                    }
                  }}
                >
                  <div style={{
                    backgroundColor: '#161b22',
                    border: '2px solid #22c55e',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.8)',
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '90vw',
                    maxHeight: '90vh'
                  }}>
                    {/* Header */}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 16px', backgroundColor: '#0f1f0f',
                      borderBottom: '1px solid #22c55e', flexShrink: 0
                    }}>
                      <span style={{ color: '#4ade80', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
                        🐢 Turtle Graphics
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {turtleRunning && (
                          <span style={{ color: '#86efac', fontSize: '12px', fontStyle: 'italic' }}>Running…</span>
                        )}
                        {!turtleRunning && turtleOutput === '✅ Done!' && (
                          <span style={{ color: '#4ade80', fontSize: '12px' }}>✅ Done</span>
                        )}
                        <button
                          onClick={() => {
                            setShowTurtlePanel(false);
                            setTurtleOutput('');
                            const c = document.getElementById('turtle-canvas-classcode');
                            if (c) c.innerHTML = '';
                          }}
                          style={{
                            background: 'none', border: '1px solid #374151', color: '#9ca3af',
                            cursor: 'pointer', fontSize: '16px', padding: '2px 10px', borderRadius: '6px',
                            lineHeight: 1.4
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = '#f87171'; }}
                          onMouseOut={(e) => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.borderColor = '#374151'; }}
                          title="Close (or click outside)"
                        >✕</button>
                      </div>
                    </div>
                    {/* Canvas */}
                    <div style={{ backgroundColor: '#ffffff', padding: '0', overflow: 'auto', flexShrink: 0 }}>
                      <div
                        id="turtle-canvas-classcode"
                        style={{ width: '1200px', height: '800px', backgroundColor: '#ffffff' }}
                      />
                    </div>
                    {/* Text output strip */}
                    {turtleOutput && turtleOutput !== '✅ Done!' && turtleOutput !== '⏳ Loading Skulpt...' && turtleOutput !== '🐢 Running...' && (
                      <div style={{
                        padding: '8px 14px', backgroundColor: '#0d1117', borderTop: '1px solid #22c55e',
                        fontFamily: 'Consolas, Monaco, monospace', fontSize: '12px', color: '#4ade80',
                        whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto', flexShrink: 0
                      }}>
                        {turtleOutput}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Student notes panel (only when viewing class files, not own files) */}
              {!isAdmin && selectedCodeId && selectedFileSource === 'class' && (
                <>
                {/* Draggable divider for memo */}
                <div
                  style={{
                    height: '6px',
                    backgroundColor: isDraggingMemoDivider.current ? '#fbbf24' : '#21262d',
                    cursor: 'row-resize',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    borderTop: '0.5px solid #d1fae5',
                    borderBottom: '0.5px solid #30363d',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fbbf24'; }}
                  onMouseLeave={(e) => { if (!isDraggingMemoDivider.current) e.currentTarget.style.backgroundColor = '#21262d'; }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isDraggingMemoDivider.current = true;
                    const startY = e.clientY;
                    const startHeight = memoHeight;
                    const container = editorOutputContainerRef.current;
                    const containerHeight = container ? container.getBoundingClientRect().height : 600;

                    const onMouseMove = (ev) => {
                      const delta = startY - ev.clientY;
                      const newHeight = Math.max(40, Math.min(containerHeight - 120, startHeight + delta));
                      setMemoHeight(newHeight);
                    };
                    const onMouseUp = () => {
                      isDraggingMemoDivider.current = false;
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                      document.body.style.userSelect = '';
                      document.body.style.cursor = '';
                    };
                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                    document.body.style.userSelect = 'none';
                    document.body.style.cursor = 'row-resize';
                  }}
                >
                  <div style={{
                    width: '36px',
                    height: '2px',
                    backgroundColor: '#484f58',
                    borderRadius: '1px'
                  }} />
                </div>
                <div style={{
                  backgroundColor: '#161b22',
                  padding: '10px 12px',
                  flexShrink: 0,
                  height: `${memoHeight}px`,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}>
                  <label style={{ color: '#fbbf24', display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', flexShrink: 0 }}>
                    📝 MY NOTES
                  </label>
                  <textarea
                    value={memos[selectedCodeId] || ''}
                    onChange={(e) => handleSaveMemo(selectedCodeId, e.target.value)}
                    placeholder="Add your personal notes about this code..."
                    style={{
                      width: '100%',
                      flex: 1,
                      padding: '8px 10px',
                      borderRadius: '4px',
                      backgroundColor: '#0d1117',
                      color: '#ffffff',
                      border: '0.5px solid #d1fae5',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '12px',
                      resize: 'none',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                </>
              )}

              {/* Tutor Help Chat Panel */}
              {showTutorHelp && (
                <>
                {/* Draggable divider for tutor chat */}
                <div
                  style={{
                    height: '6px',
                    backgroundColor: isDraggingTutorDivider.current ? '#3fb950' : '#21262d',
                    cursor: 'row-resize',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    borderTop: '2px solid #3fb950',
                    borderBottom: '0.5px solid #30363d',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#3fb950'; }}
                  onMouseLeave={(e) => { if (!isDraggingTutorDivider.current) e.currentTarget.style.backgroundColor = '#21262d'; }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isDraggingTutorDivider.current = true;
                    const startY = e.clientY;
                    const startHeight = tutorChatHeight;
                    const container = editorOutputContainerRef.current;
                    const containerHeight = container ? container.getBoundingClientRect().height : 600;

                    const onMouseMove = (ev) => {
                      const delta = startY - ev.clientY;
                      const newHeight = Math.max(150, Math.min(containerHeight - 150, startHeight + delta));
                      setTutorChatHeight(newHeight);
                    };
                    const onMouseUp = () => {
                      isDraggingTutorDivider.current = false;
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                      document.body.style.userSelect = '';
                      document.body.style.cursor = '';
                    };
                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                    document.body.style.userSelect = 'none';
                    document.body.style.cursor = 'row-resize';
                  }}
                >
                  <div style={{
                    width: '36px',
                    height: '2px',
                    backgroundColor: '#3fb950',
                    borderRadius: '1px'
                  }} />
                </div>
                <div style={{
                  backgroundColor: '#0d1117',
                  display: 'flex',
                  flexDirection: 'column',
                  height: `${tutorChatHeight}px`,
                  flexShrink: 0
                }}>
                  {/* Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: '#161b22',
                    borderBottom: '1px solid #30363d'
                  }}>
                    <span style={{ color: '#3fb950', fontWeight: '600', fontSize: '13px' }}>
                      🎓 Debugging Tutor
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => { setTutorMessages([]); }}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#ffffff',
                          border: '1px solid #30363d',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        Clear Chat
                      </button>
                      <button
                        onClick={() => { setShowTutorHelp(false); setTutorMessages([]); }}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#f85149',
                          border: 'none',
                          padding: '2px 6px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}
                        onMouseOver={(e) => e.target.style.color = '#ff0000'}
                        onMouseOut={(e) => e.target.style.color = '#f85149'}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  
                  {/* Chat messages */}
                  <div
                    ref={tutorChatRef}
                    style={{
                      flex: 1,
                      overflowY: 'auto',
                      padding: '10px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}
                  >
                    {tutorMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        style={{
                          alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                          maxWidth: '85%',
                          padding: '8px 12px',
                          borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                          backgroundColor: msg.role === 'user' ? '#166534' : '#21262d',
                          color: '#ffffff',
                          fontSize: '13px',
                          lineHeight: '1.5',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {msg.content}
                      </div>
                    ))}
                    {tutorLoading && (
                      <div style={{
                        alignSelf: 'flex-start',
                        padding: '8px 12px',
                        borderRadius: '12px 12px 12px 4px',
                        backgroundColor: '#21262d',
                        color: '#6e7681',
                        fontSize: '13px'
                      }}>
                        Thinking...
                      </div>
                    )}
                  </div>
                  
                  {/* Input area */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    padding: '10px 12px',
                    borderTop: '1px solid #30363d',
                    backgroundColor: '#161b22'
                  }}>
                    <textarea
                      value={tutorInput}
                      onChange={(e) => setTutorInput(e.target.value)}
                      placeholder="Ask the tutor for help..."
                      rows={3}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: '#0d1117',
                        color: '#ffffff',
                        border: '1px solid #30363d',
                        fontSize: '13px',
                        outline: 'none',
                        resize: 'none',
                        fontFamily: 'inherit',
                        lineHeight: '1.4'
                      }}
                    />
                    <button
                      onClick={handleTutorSend}
                      disabled={!tutorInput.trim() || tutorLoading}
                      style={{
                        backgroundColor: '#166534',
                        color: '#ffffff',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: (!tutorInput.trim() || tutorLoading) ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '16px',
                        opacity: (!tutorInput.trim() || tutorLoading) ? 0.6 : 1
                      }}
                    >
                      Send
                    </button>
                  </div>
                </div>
                </>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Right-click Context Menu with invisible backdrop */}
        {contextMenu && (
          <>
            {/* Invisible backdrop — catches clicks/right-clicks outside the menu to dismiss */}
            <div
              style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 1999, backgroundColor: 'transparent'
              }}
              onClick={() => setContextMenu(null)}
              onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}
            />
            {/* The actual menu */}
            <div
              style={{
                position: 'fixed',
                top: contextMenu.y,
                left: contextMenu.x,
                backgroundColor: '#1e1e1e',
                border: '1px solid #30363d',
                borderRadius: '6px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                zIndex: 2000,
                minWidth: '160px',
                padding: '4px 0',
                overflow: 'hidden'
              }}
            >
            {contextMenu.type === 'folder' && (
              <>
                {/* New File - only for admin in CLASS FILES, or anyone in MY FILES */}
                {(isAdmin || contextMenu.section === 'my') && (
                <div
                  onClick={() => {
                    const folder = contextMenu.name;
                    const section = contextMenu.section;
                    setActiveFolder(folder);
                    setSelectedFileSource(section);
                    setCollapsedFolders(prev => ({ ...prev, [`${section}-${folder}`]: false }));
                    if (section === 'class') {
                      setEditMode('new'); setMyFileEditMode(null);
                      setSelectedCodeId(null); setCodeName(''); setCodeContent('');
                    } else {
                      setMyFileEditMode('new'); setEditMode('new');
                      setSelectedCodeId(null); setCodeName(''); setMyFileName(''); setCodeContent('');
                    }
                    setContextMenu(null);
                  }}
                  style={{
                    padding: '8px 14px', cursor: 'pointer', color: '#ffffff', fontSize: '13px',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1f6feb33'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span>➕</span> New File
                </div>
                )}
                <div
                  onClick={async () => {
                    const folder = contextMenu.name;
                    const section = contextMenu.section;
                    // Get all files in this folder
                    const filesInFolder = codeNames.filter(cn => {
                      const isInSection = section === 'class' 
                        ? cn.createdBy === 'admin'
                        : cn.createdBy === username;
                      return isInSection && cn.name.startsWith(folder + '/');
                    });
                    
                    if (filesInFolder.length === 0) {
                      alert(`No files in folder "${folder}" to download.`);
                      setContextMenu(null);
                      return;
                    }
                    
                    try {
                      const zip = new JSZip();
                      // Fetch content for each file and add to zip
                      for (const file of filesInFolder) {
                        const res = await apiFetch(`${API_BASE_URL}/api/classCode/byId?id=${file.id}`);
                        if (res.ok) {
                          const data = await res.json();
                          if (data.success && data.code) {
                            // Remove folder prefix from filename for the zip
                            const fileName = file.name.substring(folder.length + 1);
                            zip.file(fileName, data.code.code || '');
                          }
                        }
                      }
                      // Generate and download the zip
                      const content = await zip.generateAsync({ type: 'blob' });
                      const url = URL.createObjectURL(content);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${folder}.zip`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (err) {
                      logger.error('Error downloading folder:', err);
                      alert('Error downloading folder. Please try again.');
                    }
                    setContextMenu(null);
                  }}
                  style={{
                    padding: '8px 14px', cursor: 'pointer', color: '#58a6ff', fontSize: '13px',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1f6feb33'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span>📥</span> Download Folder
                </div>
                {/* Delete Folder - only for admin in CLASS FILES, or anyone in MY FILES */}
                {(isAdmin || contextMenu.section === 'my') && (
                <div
                  onClick={() => {
                    const folder = contextMenu.name;
                    const section = contextMenu.section;
                    const sectionKey = section === 'class' ? 'class' : 'my';
                    // Check if folder has files
                    const hasFiles = codeNames.some(cn => cn.name.startsWith(folder + '/'));
                    if (hasFiles) {
                      alert(`Cannot delete folder "${folder}" — it still contains files. Delete the files first.`);
                    } else {
                      const updated = { ...emptyFolders, [sectionKey]: (emptyFolders[sectionKey] || []).filter(f => f !== folder) };
                      saveEmptyFolders(updated);
                      if (activeFolder === folder) setActiveFolder(null);
                    }
                    setContextMenu(null);
                  }}
                  style={{
                    padding: '8px 14px', cursor: 'pointer', color: '#f85149', fontSize: '13px',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8514922'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span>🗑️</span> Delete Folder
                </div>
                )}
              </>
            )}
            {contextMenu.type === 'file' && (
              <div
                onClick={async () => {
                  const fileId = contextMenu.id;
                  const fileName = contextMenu.name;
                  if (window.confirm(`Delete "${fileName}"?`)) {
                    try {
                      const res = await apiFetch(`${API_BASE_URL}/api/classCode/delete`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: fileId })
                      });
                      const data = await res.json();
                      if (res.ok && data.success) {
                        await loadCodes();
                        await loadCodeNames();
                        if (selectedCodeId === fileId) {
                          setSelectedCodeId(null); setCodeContent(''); setCodeName('');
                          setEditMode(null); setMyFileEditMode(null);
                        }
                      } else {
                        setError(data.error || 'Failed to delete file');
                      }
                    } catch (err) { setError('Failed to delete file'); }
                  }
                  setContextMenu(null);
                }}
                style={{
                  padding: '8px 14px', cursor: 'pointer', color: '#f85149', fontSize: '13px',
                  display: 'flex', alignItems: 'center', gap: '8px'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8514922'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span>🗑️</span> Delete
              </div>
            )}
          </div>
          </>
        )}

        {/* Explanation Modal */}
        {showExplanationModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1100
          }}>
            <div style={{
              backgroundColor: '#1e1e1e',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '80vh',
              overflow: 'auto',
              border: '0.5px solid #d1fae5'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <h3 style={{ margin: 0, color: '#ffffff' }}>💡 Code Explanation</h3>
                <button
                  onClick={() => setShowExplanationModal(false)}
                  style={{
                    ...buttonStyle,
                    backgroundColor: 'transparent',
                    color: '#ffffff',
                    fontSize: '18px'
                  }}
                  onMouseOver={(e) => e.target.style.color = '#f85149'}
                  onMouseOut={(e) => e.target.style.color = '#ffffff'}
                >
                  ✕
                </button>
              </div>

              {/* Show the full code first */}
              {codeContent.trim() && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: '#fbbf24', fontSize: '12px', fontWeight: '700', marginBottom: '6px', letterSpacing: '0.5px' }}>
                    📄 YOUR CODE
                  </div>
                  <CodeBlock
                    code={codeContent}
                    style={{
                      backgroundColor: '#0d1117',
                      border: '1px solid #30363d',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      margin: 0,
                      overflowX: 'auto',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '13px',
                      lineHeight: '1.5',
                      color: '#ffffff',
                      whiteSpace: 'pre',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      tabSize: 4
                    }}
                  />
                </div>
              )}

              {/* Divider */}
              <div style={{ borderTop: '1px solid #30363d', margin: '12px 0' }} />

              <div style={{ color: '#fbbf24', fontSize: '12px', fontWeight: '700', marginBottom: '8px', letterSpacing: '0.5px' }}>
                📝 EXPLANATION
              </div>

              {explaining ? (
                <div style={{ textAlign: 'center', color: '#fbbf24', padding: '30px' }}>
                  🤔 Generating explanation...
                </div>
              ) : (
                <div style={{
                  backgroundColor: '#161b22',
                  borderRadius: '8px',
                  padding: '15px',
                  color: '#ffffff',
                  fontSize: '17px',
                  lineHeight: '1.6'
                }}>
                  {(() => {
                    // Parse markdown: split on ```language ... ``` code blocks
                    const parts = (explanation || '').split(/(```[\s\S]*?```)/g);
                    return parts.map((part, idx) => {
                      if (part.startsWith('```') && part.endsWith('```')) {
                        // Extract language and code
                        const firstNewline = part.indexOf('\n');
                        const code = firstNewline !== -1
                          ? part.substring(firstNewline + 1, part.length - 3)
                          : part.substring(3, part.length - 3);
                        return (
                          <CodeBlock
                            key={idx}
                            code={code}
                            style={{
                              backgroundColor: '#0d1117',
                              border: '1px solid #30363d',
                              borderRadius: '6px',
                              padding: '12px 16px',
                              margin: '10px 0',
                              overflowX: 'auto',
                              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                              fontSize: '13px',
                              lineHeight: '1.5',
                              color: '#ffffff',
                              whiteSpace: 'pre',
                              tabSize: 4
                            }}
                          />
                        );
                      }
                      // Regular text: handle inline code (`...`), bold (**...**), and newlines
                      const inlineParts = part.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
                      return (
                        <span key={idx} style={{ whiteSpace: 'pre-wrap' }}>
                          {inlineParts.map((seg, sIdx) => {
                            if (seg.startsWith('`') && seg.endsWith('`')) {
                              return (
                                <code key={sIdx} style={{
                                  backgroundColor: '#0d1117',
                                  border: '1px solid #30363d',
                                  borderRadius: '3px',
                                  padding: '2px 6px',
                                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                  fontSize: '12px',
                                  color: '#ffa657'
                                }}>
                                  {seg.slice(1, -1)}
                                </code>
                              );
                            }
                            if (seg.startsWith('**') && seg.endsWith('**')) {
                              return <strong key={sIdx} style={{ color: '#f0f6fc' }}>{seg.slice(2, -2)}</strong>;
                            }
                            return <span key={sIdx}>{seg}</span>;
                          })}
                        </span>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Input pre-supply modal for Visualizer */}
    {showInputModal && (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 12000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ backgroundColor: '#161b22', border: '1px solid rgba(255, 240, 150, 0.6)', borderRadius: '10px', padding: '0', width: '780px', maxWidth: '95vw', overflow: 'hidden' }}>
          <div style={{ backgroundColor: 'rgba(255,240,150,0.07)', borderBottom: '1px solid rgba(255,240,150,0.2)', padding: '20px 24px' }}>
            <h3 style={{ color: '#f0e68c', margin: 0, fontSize: '22px' }}>Supply inputs for Visualizer</h3>
            <p style={{ color: '#c9ba7a', fontSize: '17px', marginBottom: '0', marginTop: '8px' }}>Enter one value per line — supply as many as needed (even if the count depends on earlier inputs):</p>
          </div>
          <div style={{ padding: '16px 20px 20px' }}>
          <textarea
            autoFocus
            value={inputModalValues}
            onChange={e => setInputModalValues(e.target.value)}
            placeholder={`value 1\nvalue 2\nvalue 3\n...`}
            rows={16}
            style={{ width: '100%', padding: '10px 14px', backgroundColor: '#0d1117', border: '1px solid rgba(255, 240, 150, 0.5)', borderRadius: '6px', color: '#e6edf3', fontSize: '22px', boxSizing: 'border-box', fontFamily: 'monospace', resize: 'vertical', outline: 'none' }}
          />
          <p style={{ color: '#c9ba7a', fontSize: '17px', marginTop: '10px', marginBottom: '0', lineHeight: 1.5 }}>
            💡 <strong style={{ color: '#e8d48a' }}>Tip:</strong> Run the code first to see what it asks for.
            If your code uses <code style={{ color: '#79c0ff' }}>random</code>, the visualizer uses fixed values — run it once to discover them, then supply the matching inputs here.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button onClick={() => setShowInputModal(false)} style={{ padding: '7px 16px', backgroundColor: '#21262d', color: '#c9d1d9', border: '1px solid rgba(255, 240, 150, 0.35)', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => runTrace(pendingTraceCode, inputModalValues.split('\n'))} style={{ padding: '7px 16px', backgroundColor: '#1d3a5f', color: '#79c0ff', border: '1px solid rgba(255,240,150,0.75)', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>🔍 Run Visualizer</button>
          </div>
          </div>
        </div>
      </div>
    )}

    {/* VisualDebugger */}
    {showVisualizer && traceSteps.length > 0 && (
      <VisualDebugger
        code={codeContent}
        steps={traceSteps}
        onClose={() => setShowVisualizer(false)}
      />
    )}
    </>
  );
};

export default ClassCodeModal;
